import "./App.css";
import Navbar from "@funded-here-interface/common/src/components/MobileNavbar/Navbar";
import OfflineBanner from "@funded-here-interface/common/src/components/OfflineBanner";
import { setInvestorToken } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { useVersionCheck } from "@funded-here-interface/common/src/hooks/useVersionCheck";
import { JwtProvider } from "@funded-here-interface/common/src/providers/JwtProvider";
import { SessionInterceptorProvider } from "@funded-here-interface/common/src/providers/SessionInterceptorProvider";
import { Center, Loader } from "@mantine/core";
import { lazy, Suspense } from "react";
import { QueryClientProvider } from "react-query";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";

// Eager, unlike the screens: a lazy() layout would suspend on the first
// /login <-> /registration navigation and tear down the brand panel it slides.
import AuthSplitLayout from "./components/AuthSplitLayout";
import { privateRoutes, renderWithLayout } from "./privateRoutes";
import { client } from "./reactQuery";
import { store, persistor, RootState } from "./store";
import { investorResetActions } from "./Utils/investorResetActions";

import "react-toastify/dist/ReactToastify.css";

const AcknowledgementLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/AcknowledgementLayout/AcknowledgementLayout"),
);
const AuthLayout = lazy(() => import("./components/AuthLayout/AuthLayout"));

const PasswordReset = lazy(
  () => import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordReset"),
);
const PasswordResetMsg = lazy(
  () => import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordResetMsg"),
);
const TwoFAQRPageCommon = lazy(
  () => import("@funded-here-interface/common/src/pages/TwoFA/TwoFAQRCode"),
);

// Named factories rather than inline arrows: AuthSplitLayout calls the OTHER
// screen's factory to warm its chunk, so the first /login <-> /registration
// switch slides a real form instead of a Suspense loader. The same factory
// goes to lazy() and to the layout's `preloadOther`, so both share one
// cached `import()`. Merchant portal screens get the same treatment through
// `gatedRoute` in privateRoutes.tsx: MerchantAccountGate downloads them
// alongside the status check.
const importLogin = () => import("./Screens/Login/Login");
const Login = lazy(importLogin);
const NewPassword = lazy(() => import("./Screens/NewPassword/NewPassword"));
const PasswordChangedMessage = lazy(() => import("./Screens/NewPassword/PasswordChangedMessage"));
const importRegistration = () => import("./Screens/Registration/Registration");
const Registration = lazy(importRegistration);
const OtpVerification = lazy(() => import("./Screens/OTPverification/OtpVerification"));

const SuspenseFallback = (
  <Center style={{ height: "100vh" }}>
    <Loader size="lg" />
  </Center>
);

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  return !token || token === "none" ? <Navigate to="/login" replace /> : <>{children}</>;
};

function App() {
  useVersionCheck();

  return (
    <QueryClientProvider client={client}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ToastContainer />
          <OfflineBanner />
          <BrowserRouter>
            <SessionInterceptorProvider
              persistor={persistor}
              resetActions={investorResetActions}
              tokenSelector={(state: RootState) => state.auth}
            >
              <JwtProvider
                tokenSelector={(state: RootState) => state.auth}
                setTokenAction={setInvestorToken}
                resetActions={investorResetActions}
              >
                <Suspense fallback={SuspenseFallback}>
                  <Routes>
                    {/* Public routes */}
                    {/* Both auth screens render the SAME AuthSplitLayout at the
                        same position in the tree, so React reconciles it across
                        the navigation instead of remounting — that is what lets
                        the brand illustration slide from one side to the other
                        rather than being destroyed and rebuilt. `side` is which
                        half the form occupies, and it drives the slide. */}
                    <Route
                      path="/registration"
                      element={
                        <AuthSplitLayout
                          side="left"
                          content={<Registration />}
                          formLabel="Sign up"
                          preloadOther={importLogin}
                        />
                      }
                    />
                    <Route
                      path="/two-auth-QR-code"
                      element={<Navbar content={<TwoFAQRPageCommon />} />}
                    />
                    <Route
                      path="/login/:redirectPath?"
                      element={
                        <AuthSplitLayout
                          side="right"
                          content={<Login />}
                          formLabel="Log in"
                          preloadOther={importRegistration}
                        />
                      }
                    />
                    <Route
                      path="/password-reset"
                      element={<AuthLayout content={<PasswordReset />} />}
                    />
                    <Route
                      path="/reset-message"
                      element={<AcknowledgementLayout content={<PasswordResetMsg />} />}
                    />
                    <Route
                      path="/reset-password"
                      element={<AuthLayout content={<NewPassword title="Reset Your Password" />} />}
                    />
                    <Route
                      path="/new-password"
                      element={<AuthLayout content={<NewPassword title="Password Creation" />} />}
                    />
                    <Route
                      path="/password-changed"
                      element={<AuthLayout content={<PasswordChangedMessage />} />}
                    />
                    <Route path="/otp-verification" element={<OtpVerification />} />

                    {/* Private routes */}
                    {privateRoutes.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<PrivateRoute>{renderWithLayout(route)}</PrivateRoute>}
                      />
                    ))}

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </Suspense>
              </JwtProvider>
            </SessionInterceptorProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
